import React, { useEffect, useRef } from "react";

interface PairBlendCanvasProps {
    ab: [string, string];
    cd: [ImageBitmap, ImageBitmap];
    duration?: number;
    width?: number;
    height?: number;
    loop?: boolean;
    func?: ( ab: React.RefObject<HTMLCanvasElement>, cd: React.RefObject<HTMLCanvasElement> ) => React.ReactNode;
}

export const PairBlendCanvas: React.FC<PairBlendCanvasProps> = ( {
    ab,
    cd,
    duration = 2000,
    width = 800,
    height = 600,
    loop = true,
    func = () => null
} ) => {
    const canvasAB = useRef<HTMLCanvasElement>( null );
    const canvasCD = useRef<HTMLCanvasElement>( null );
    const animationRef = useRef<number>();

    useEffect( () => {
        let cancelled = false;

        async function loadImage( src: string ) {
            const img = new Image();
            await new Promise<void>( ( resolve, reject ) => {
                img.onload = () => resolve();
                img.onerror = reject;
                img.src = src;
            } );
            return img;
        }

        async function run() {
            const [a, b] = await Promise.all( [
                loadImage( ab[0] ),
                loadImage( ab[1] ),
            ] );
            const [c, d] = [cd[0], cd[1]];
            if( cancelled ) { return; }

            const start = performance.now();

            const animate = ( now: number ) => {
                const cycle = duration * 2;
                const t = loop
                    ? ( ( now - start ) % cycle ) / duration
                    : Math.min( ( now - start ) / duration, 1 );

                const raw = t <= 1 ? t : 2 - t;
                const blend = easeInOutCubic( raw );

                drawBlend( canvasAB.current, a, b, blend, width, height );
                drawBlend( canvasCD.current, c, d, blend, width, height );

                if( loop || raw < 1 ) {
                    animationRef.current = requestAnimationFrame( animate );
                }
            };

            animationRef.current = requestAnimationFrame( animate );
        }

        run().catch( console.error );

        return () => {
            cancelled = true;
            if( animationRef.current ) {
                cancelAnimationFrame( animationRef.current );
            }
        };
    }, [ab, cd, duration, width, height, loop] );    
    return <>
        <canvas ref={canvasAB} width={width} height={height} className="border border-2 rounded-4" style={{ backgroundColor: "#000" }} />
        <canvas ref={canvasCD} width={width} height={height} className="border border-2 rounded-4" style={{ backgroundColor: "#000" }} />
    </>;
};    

function drawBlend(
    canvas: HTMLCanvasElement | null,
    imgA: BlendImage,
    imgB: BlendImage,
    blend: number,
    width: number,
    height: number
) {
    if( !canvas ) { return; }

    const ctx = canvas.getContext( "2d" );
    if( !ctx ) { return; }

    ctx.fillStyle = "#000";
    ctx.fillRect( 0, 0, width, height );
    // ctx.clearRect( 0, 0, width, height );
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const dims = calculateDrawDimensions( imgA, width, height );

    ctx.globalAlpha = 1;
    ctx.drawImage( imgA, dims.offsetX, dims.offsetY, dims.drawWidth, dims.drawHeight );

    ctx.globalAlpha = blend;
    ctx.drawImage( imgB, dims.offsetX, dims.offsetY, dims.drawWidth, dims.drawHeight );

    ctx.globalAlpha = 1;
}

function easeInOutCubic( t: number ): number {
    return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow( -2 * t + 2, 3 ) / 2;
}

type BlendImage = HTMLImageElement | ImageBitmap;

function calculateDrawDimensions(
    img: BlendImage,
    canvasWidth: number,
    canvasHeight: number
) {    
    const imgAspect = img.width / img.height;
    const canvasAspect = canvasWidth / canvasHeight;

    if( imgAspect > canvasAspect ) {
        const drawWidth = canvasWidth;
        const drawHeight = canvasWidth / imgAspect;
        return { drawWidth, drawHeight, offsetX: 0, offsetY: ( canvasHeight - drawHeight ) / 2 };
    }

    const drawHeight = canvasHeight;
    const drawWidth = canvasHeight * imgAspect;
    return { drawWidth, drawHeight, offsetX: ( canvasWidth - drawWidth ) / 2, offsetY: 0 };
}